/**
 * Find Your Stack — v2 adaptive quiz + dynamic recommendation engine.
 *
 * Flow
 *  1. Questions (rendered in Liquid) score outcome keys and set preference
 *     facets (diet, format, caffeine, budget, level, skip, brand ...).
 *  2. The winning result (from the JSON config emitted by the section) is a
 *     recipe of SLOTS. For each slot we fetch a lean JSON feed of in-stock
 *     products per source collection (templates/collection.quiz-feed.liquid),
 *     filter by tags / diet / caffeine, score by format, price band, reviews,
 *     popularity and brand, dedupe across the stack, and pick the best match.
 *  3. Results render client-side: per-slot cards with flavour select, swap
 *     sheet (next-best alternatives), remove/add-back, running totals with the
 *     stack saving and free-delivery progress, a routine timeline, alternative
 *     stacks, share link, email capture and one-tap add-to-cart.
 *
 * Answers persist in localStorage (gs-quiz-v2) and encode into ?a= links so a
 * shared or saved link re-personalises against live stock.
 */
(function () {
  'use strict';

  var CART_ADD_URL = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js';
  var CART_JS_URL = '/cart.js';
  var STORAGE_KEY = 'gs-quiz-v2';
  var FEED_TTL = 30 * 60 * 1000;
  var FEED_TIMEOUT = 9000;
  var TIMING_ORDER = ['morning', 'pre', 'post', 'food', 'anytime', 'evening'];

  /* ------------------------------------------------------------------ utils */

  function qs(root, sel) { return root.querySelector(sel); }
  function qsa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  function h(tag, attrs, children) {
    var el = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        var v = attrs[k];
        if (v === null || v === undefined || v === false) return;
        if (k === 'class') el.className = v;
        else if (k === 'text') el.textContent = v;
        else if (k === 'html') el.innerHTML = v;
        else if (k.indexOf('on') === 0 && typeof v === 'function') el.addEventListener(k.slice(2), v);
        else el.setAttribute(k, v === true ? '' : v);
      });
    }
    if (children) {
      (Array.isArray(children) ? children : [children]).forEach(function (c) {
        if (c === null || c === undefined || c === false) return;
        el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
      });
    }
    return el;
  }

  function splitList(str) {
    return String(str || '').split(',').map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean);
  }

  function parseOutcomes(str) {
    return String(str || '').split(',').map(function (s) {
      var parts = s.trim().split(':');
      var key = (parts[0] || '').trim();
      var w = parseFloat(parts[1]);
      return key ? { k: key, w: isNaN(w) ? 1 : w } : null;
    }).filter(Boolean);
  }

  // "goal=strength; fit=1; skip=protein,creatine" -> [{k,v}, ...]
  function parseFacets(str) {
    var out = [];
    String(str || '').split(';').forEach(function (pair) {
      var idx = pair.indexOf('=');
      if (idx === -1) {
        var bare = pair.trim().toLowerCase();
        if (bare) out.push({ k: bare, v: '1' });
        return;
      }
      var k = pair.slice(0, idx).trim().toLowerCase();
      pair.slice(idx + 1).split(',').forEach(function (v) {
        v = v.trim().toLowerCase();
        if (k && v) out.push({ k: k, v: v });
      });
    });
    return out;
  }

  function slug(str) {
    return String(str || '').toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function fill(template, vars) {
    // Supports "[count]" (theme settings strip Liquid braces) and "{{ count }}".
    return String(template || '').replace(/\[\s*(\w+)\s*\]|\{\{\s*(\w+)\s*\}\}/g, function (_, a, b) {
      var k = a || b;
      return vars[k] !== undefined ? vars[k] : '';
    });
  }

  function formatMoney(cents, format) {
    var value = (Number(cents) || 0) / 100;
    var fmt = format || '£{{amount}}';
    function withSep(num, decimals, thousands, decimal) {
      var fixed = num.toFixed(decimals);
      var parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      switch (key) {
        case 'amount_no_decimals': return withSep(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withSep(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withSep(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withSep(value, 2, "'", '.');
        default: return withSep(value, 2, ',', '.');
      }
    });
  }

  function debounceFrame(fn) {
    var raf = null;
    return function () {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(fn);
    };
  }

  /* -------------------------------------------------------------- analytics */

  function makeTracker(enabled) {
    return function track(name, data) {
      if (!enabled) return;
      var payload = data || {};
      try {
        window.dataLayer = window.dataLayer || [];
        var row = { event: 'gs_quiz_' + name };
        Object.keys(payload).forEach(function (k) { row[k] = payload[k]; });
        window.dataLayer.push(row);
      } catch (e) { /* noop */ }
      try {
        if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
          Shopify.analytics.publish('gs_quiz_' + name, payload);
        }
      } catch (e) { /* noop */ }
      try {
        document.dispatchEvent(new CustomEvent('gs:quiz', { detail: { name: name, data: payload } }));
      } catch (e) { /* noop */ }
    };
  }

  /* ------------------------------------------------------------------- cart */

  function addToCart(items) {
    var cartDrawer = document.querySelector('cart-drawer');
    var sections = 'cart-icon-bubble';
    if (cartDrawer) {
      var section = cartDrawer.closest('.shopify-section');
      if (section) sections += ',' + section.id.replace('shopify-section-', '');
    }
    return fetch(CART_ADD_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: items, sections: sections, sections_url: window.location.pathname })
    }).then(function (response) {
      if (!response.ok) throw new Error(response.status);
      return response.json();
    }).then(function (data) {
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(data, true);
      } else {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
      }
      return data;
    });
  }

  function fetchCart() {
    return fetch(CART_JS_URL, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  /* ------------------------------------------------------------------- feed */

  var feedMemory = {};

  function readFeedCache(handle) {
    try {
      var raw = sessionStorage.getItem('gsq-feed:' + handle);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || !data.t || Date.now() - data.t > FEED_TTL || !Array.isArray(data.p)) return null;
      return data.p;
    } catch (e) { return null; }
  }

  function writeFeedCache(handle, products) {
    try { sessionStorage.setItem('gsq-feed:' + handle, JSON.stringify({ t: Date.now(), p: products })); } catch (e) { /* quota */ }
  }

  function fetchWithTimeout(url, ms) {
    var controller = window.AbortController ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, ms) : null;
    return fetch(url, { credentials: 'same-origin', signal: controller ? controller.signal : undefined })
      .then(function (r) { if (timer) clearTimeout(timer); return r; }, function (e) { if (timer) clearTimeout(timer); throw e; });
  }

  function normaliseProductsJson(json) {
    // Fallback shape from /collections/<h>/products.json
    return (json.products || []).map(function (p) {
      var v = (p.variants || []).filter(function (x) { return x.available; })[0];
      if (!v) return null;
      return {
        id: p.id, h: p.handle, t: p.title, v: p.vendor, ty: p.product_type, tags: p.tags || [],
        p: Math.round(parseFloat(v.price) * 100), cp: v.compare_at_price ? Math.round(parseFloat(v.compare_at_price) * 100) : 0,
        vid: v.id, vt: v.title, u: '/products/' + p.handle,
        img: p.images && p.images[0] ? p.images[0].src : '', b: '', s: '', f: '', r: 0, rc: 0,
        vars: (p.variants || []).filter(function (x) { return x.available; }).map(function (x) {
          return { id: x.id, t: x.title, p: Math.round(parseFloat(x.price) * 100), cp: x.compare_at_price ? Math.round(parseFloat(x.compare_at_price) * 100) : 0 };
        })
      };
    }).filter(Boolean);
  }

  function fetchFeed(handle) {
    if (feedMemory[handle]) return feedMemory[handle];
    var cached = readFeedCache(handle);
    if (cached) {
      feedMemory[handle] = Promise.resolve(cached);
      return feedMemory[handle];
    }
    var url = '/collections/' + encodeURIComponent(handle) + '?view=quiz-feed&sort_by=best-selling';
    feedMemory[handle] = fetchWithTimeout(url, FEED_TIMEOUT)
      .then(function (r) {
        if (!r.ok) throw new Error('feed ' + r.status);
        return r.text();
      })
      .then(function (text) {
        var json = JSON.parse(text);
        if (!json || !Array.isArray(json.products)) throw new Error('bad feed');
        return json.products;
      })
      .catch(function () {
        // Fallback: public products.json (no metafields, may be rate limited).
        return fetchWithTimeout('/collections/' + encodeURIComponent(handle) + '/products.json?limit=48', FEED_TIMEOUT)
          .then(function (r) { return r.ok ? r.json() : { products: [] }; })
          .then(normaliseProductsJson)
          .catch(function () { return []; });
      })
      .then(function (products) {
        if (products.length) writeFeedCache(handle, products);
        return products;
      });
    return feedMemory[handle];
  }

  /* --------------------------------------------------------- product helpers */

  var SIZE_RE = /^(.*?\b\d+(?:[.,]\d+)?\s?(?:x\s?\d+\s?)?(?:kg|g|ml|l|caps?|capsules?|vcaps|veg\s?caps|tabs?|tablets?|softgels?|gummies|servings?|serv|sachets?|scoops?|bars?|pcs?|pack|pieces?|lozenges?|chews?|softchews))\b/i;

  function tagSet(p) {
    if (!p._tags) {
      p._tags = {};
      (p.tags || []).forEach(function (t) { p._tags[String(t).toLowerCase().trim()] = true; });
      p._typeTag = (p.tags || []).map(function (t) { return String(t).toLowerCase(); }).filter(function (t) { return t.indexOf('type:') === 0; })[0] || ('type:' + slug(p.ty || 'other'));
    }
    return p._tags;
  }

  function hasTag(p, tag) { return !!tagSet(p)[tag]; }
  function hasAnyTag(p, list) {
    var tags = tagSet(p);
    for (var i = 0; i < list.length; i++) if (tags[list[i]]) return true;
    return false;
  }
  function typeTag(p) { tagSet(p); return p._typeTag; }

  function textOf(p) {
    if (!p._text) p._text = ((p.t || '') + ' ' + (p.ty || '')).toLowerCase();
    return p._text;
  }

  function detectFormat(p) {
    if (p._fmt) return p._fmt;
    var text = textOf(p);
    var tt = typeTag(p);
    var f = 'other';
    if (/\b(caps?|capsules?|vcaps|veg\s?caps|tabs?|tablets?|softgels?|gummies|gummy|chewables?|lozenges?)\b/.test(text)) f = 'caps';
    else if (/type:(rtd|energy-drink|energy-shot|protein-shake-rtd)/.test(tt) || /\b\d+\s?ml\b|ready to drink|\brtd\b|\bshot\b|\bdrink\b/.test(text)) f = 'drink';
    else if (/type:(protein-bar|protein-snack|protein-cookie|nut-butter|spreads-and-sauces)/.test(tt) || /\b(bar|bars|cookie|cookies|ball|balls|brownie|wafer|crisps|chips|flapjack|spread|butter|snack|bites?)\b/.test(text)) f = 'snack';
    else if (/\b\d+(\.\d+)?\s?(g|kg)\b/.test(text) || /powder/.test(text)) f = 'powder';
    p._fmt = f;
    return f;
  }

  function familyKey(p) {
    if (p._fam) return p._fam;
    var fam = '';
    if (p.f) fam = p.f;
    else {
      var title = String(p.t || '').replace(/\s+/g, ' ').trim();
      var m = SIZE_RE.exec(title);
      if (m) fam = m[1];
      else if (title.indexOf(' - ') !== -1) fam = title.split(' - ')[0];
      else fam = title;
    }
    p._fam = slug(p.v) + '|' + slug(fam);
    return p._fam;
  }

  function flavourOf(p) {
    if (p._flav !== undefined) return p._flav;
    var title = String(p.t || '').replace(/\s+/g, ' ').trim();
    var m = SIZE_RE.exec(title);
    var rest = m ? title.slice(m[0].length) : '';
    rest = rest.replace(/^[\s\-–—,:|]+/, '').replace(/[\s\-–—,:|]+$/, '').trim();
    if (!rest && p.vt && p.vt !== 'Default Title') rest = p.vt;
    p._flav = rest;
    return rest;
  }

  function isStimFree(p) {
    return hasTag(p, 'lifestyle:stim-free') || /stim[\s-]?free|caffeine[\s-]?free|non[\s-]?stim|decaf|\bpump\b/.test(textOf(p));
  }

  function isVeganish(p) {
    return hasTag(p, 'lifestyle:vegan') || hasTag(p, 'type:vegan-protein') || /\bvegan\b|plant[\s-]?based|\bplant protein\b|\balgae\b/.test(textOf(p));
  }

  function isAnimalDerived(p) {
    var tt = typeTag(p);
    return /type:(whey-protein|whey-isolate|casein-protein|beef-protein|collagen|omega-3|mass-gainer|protein-shake-rtd)/.test(tt)
      || /\bwhey\b|\bcasein\b|\bcollagen\b|fish oil|krill|\bbeef\b|\begg\b/.test(textOf(p));
  }

  function isDairy(p) {
    var tt = typeTag(p);
    return /type:(whey-protein|whey-isolate|casein-protein|mass-gainer|protein-shake-rtd|protein-bar|protein-cookie|protein-snack)/.test(tt)
      || /\bwhey\b|\bcasein\b|\bmilk\b/.test(textOf(p));
  }

  function dietPass(p, diet, strict) {
    if (!diet || diet === 'any') return true;
    if (diet === 'vegan') {
      if (isVeganish(p)) return true;
      return strict ? !isAnimalDerived(p) && !isDairy(p) : true;
    }
    if (diet === 'vegetarian') {
      if (!strict) return true;
      if (hasTag(p, 'lifestyle:vegetarian') || isVeganish(p)) return true;
      return !/type:(beef-protein|collagen|omega-3)/.test(typeTag(p)) && !/fish oil|krill|\bbeef\b|\bcollagen\b/.test(textOf(p));
    }
    if (diet === 'dairyfree') {
      if (!strict) return true;
      if (hasTag(p, 'lifestyle:dairy-free') || isVeganish(p) || /dairy[\s-]?free|lactose[\s-]?free/.test(textOf(p))) return true;
      return !isDairy(p);
    }
    return true;
  }

  // Topical / non-ingestible products that share collections with supplements.
  var TOPICAL_RE = /\b(bath|flakes|spray|lotion|cream|balm|body oil|massage|shampoo|conditioner|soap|topical|roll[\s-]?on|patch|patches|serum|deodorant)\b/;
  // Bulk cases and multipacks (fine for snack slots, poor default elsewhere).
  var MULTIPACK_RE = /\b\d+\s?x\s?\d+|\bcase of\b|\bbox of\b|\bmultipack\b|\bbundle\b|\b(6|10|12|18|20|24)\s?pack\b/;

  function isTopical(p) { return TOPICAL_RE.test(textOf(p)); }
  function isMultipack(p) { return MULTIPACK_RE.test(textOf(p)); }

  function dietBonus(p, diet) {
    if (!diet || diet === 'any') return 0;
    if (diet === 'vegan' && isVeganish(p)) return 6;
    if (diet === 'vegetarian' && (hasTag(p, 'lifestyle:vegetarian') || isVeganish(p))) return 5;
    if (diet === 'dairyfree' && (hasTag(p, 'lifestyle:dairy-free') || isVeganish(p))) return 5;
    if (diet === 'lowsugar' && (hasTag(p, 'lifestyle:sugar-free') || /sugar[\s-]?free|zero sugar|no added sugar/.test(textOf(p)))) return 8;
    return 0;
  }

  /* ----------------------------------------------------------------- engine */

  function firstFacet(facets, key) { return facets[key] && facets[key].length ? facets[key][0] : null; }
  function facetHas(facets, key, value) { return !!(facets[key] && facets[key].indexOf(value) !== -1); }

  function targetSlotCount(facets) {
    var budget = firstFacet(facets, 'budget') || 'any';
    var level = firstFacet(facets, 'level') || 'regular';
    var target = { low: 3, mid: 4, high: 5, any: 4 }[budget] || 4;
    if (level === 'beginner') target -= 1;
    if (level === 'advanced') target += 1;
    return Math.max(3, Math.min(5, target));
  }

  function selectSlots(result, facets) {
    var skip = facets.skip || [];
    var slots = (result.slots || []).map(function (s, i) {
      return {
        i: i, label: s.label, key: (s.key || '').toLowerCase(), cols: splitList(s.cols), inc: splitList(s.inc), exc: splitList(s.exc),
        why: s.why || '', when: s.when || 'anytime', fmt: s.fmt || 'any', diet: !!s.diet, stim: !!s.stim, opt: !!s.opt
      };
    }).filter(function (s) { return s.cols.length && !(s.key && skip.indexOf(s.key) !== -1); });

    var caffeine = firstFacet(facets, 'caffeine');
    var target = targetSlotCount(facets);
    // Drop optional slots (from the end) first, then trailing slots.
    for (var i = slots.length - 1; i >= 0 && slots.length > target; i--) {
      if (slots[i].opt) slots.splice(i, 1);
    }
    while (slots.length > target) slots.pop();
    // Caffeine avoiders: stim slots stay only if a stim-free pick exists (checked during ranking).
    slots.forEach(function (s) { s.caffeine = caffeine; });
    return slots;
  }

  function scoreCandidate(p, slot, facets, ctx) {
    var score = 0;
    score += Math.max(0, 30 - p._idx) * 0.6;

    var fmt = detectFormat(p);
    var pref = firstFacet(facets, 'format');
    if (pref && pref !== 'any') {
      var prefMatch = pref === 'snack' ? (fmt === 'snack' || fmt === 'drink') : fmt === pref;
      score += prefMatch ? 12 : -5;
    }
    if (slot.fmt && slot.fmt !== 'any') {
      var slotMatch = slot.fmt === 'snack' ? (fmt === 'snack' || fmt === 'drink') : fmt === slot.fmt;
      score += slotMatch ? 6 : -3;
    }

    var budget = firstFacet(facets, 'budget') || 'any';
    var pct = ctx.pricePct(p.p);
    if (budget === 'low') score += 14 * (1 - pct);
    else if (budget === 'mid') score += 8 * (1 - Math.abs(pct - 0.35) * 2);
    else if (budget === 'high') score += 6 * (1 - Math.abs(pct - 0.65) * 2); // upper-mid, not the priciest
    else score += 4 * (1 - Math.abs(pct - 0.4) * 2);

    if (p.b) score += 6;
    if (p.r >= 4) score += Math.min(6, (p.r - 3.5) * 4) + Math.min(3, Math.log((p.rc || 0) + 1));
    if (p.cp && p.cp > p.p) score += 3;

    var brands = facets.brand || [];
    if (brands.length && brands.indexOf(slug(p.v)) !== -1) score += 10;

    if (ctx.used.vendors[slug(p.v)]) score -= 6;
    var diet = firstFacet(facets, 'diet');
    score += dietBonus(p, diet);
    // With no dietary restriction, plant-based alternatives are a niche pick.
    if ((!diet || diet === 'any') && slot.diet && isVeganish(p)) score -= 8;
    var snackSlot = slot.fmt === 'snack' || pref === 'snack';
    if (!snackSlot && isMultipack(p)) score -= 14;

    if (slot.stim && slot.caffeine === 'sensitive' && isStimFree(p)) score += 12;
    if (/\bsample\b|\btrial\b|\bsachet\b|single serving/.test(textOf(p))) score -= 10;
    return score;
  }

  function passesHardRules(p, slot, facets, used) {
    if (!p.vid) return false;
    if (hasTag(p, 'dept:accessories') || hasTag(p, 'type:shaker') || hasTag(p, 'out of stock')) return false;
    if (isTopical(p)) return false;
    if (slot.inc.length && !hasAnyTag(p, slot.inc)) return false;
    if (slot.exc.length && hasAnyTag(p, slot.exc)) return false;
    if (used.ids[p.id] || used.families[familyKey(p)] || used.types[typeTag(p)]) return false;
    var diet = firstFacet(facets, 'diet');
    if (!dietPass(p, diet, slot.diet)) return false;
    if (slot.stim && slot.caffeine === 'avoid' && !isStimFree(p)) return false;
    return true;
  }

  function rankSlot(slot, pool, facets, used) {
    var candidates = pool.filter(function (p) { return passesHardRules(p, slot, facets, used); });
    if (!candidates.length) return null;
    var prices = candidates.map(function (p) { return p.p; }).sort(function (a, b) { return a - b; });
    var ctx = {
      used: used,
      pricePct: function (price) {
        if (prices.length < 2) return 0.5;
        var below = 0;
        for (var i = 0; i < prices.length; i++) if (prices[i] < price) below++;
        return below / (prices.length - 1);
      }
    };
    candidates.forEach(function (p) { p._score = scoreCandidate(p, slot, facets, ctx); });
    candidates.sort(function (a, b) { return b._score - a._score || a._idx - b._idx; });

    var pick = candidates[0];
    var alts = [];
    var seenFam = {};
    seenFam[familyKey(pick)] = true;
    for (var i = 1; i < candidates.length && alts.length < 4; i++) {
      var fam = familyKey(candidates[i]);
      if (seenFam[fam]) continue;
      seenFam[fam] = true;
      alts.push(candidates[i]);
    }
    var flavours = pool.filter(function (p) { return familyKey(p) === familyKey(pick) && p.vid; })
      .sort(function (a, b) { return (flavourOf(a) || '').localeCompare(flavourOf(b) || ''); });
    return { pick: pick, alts: alts, flavours: flavours };
  }

  function markUsed(used, p) {
    used.ids[p.id] = true;
    used.families[familyKey(p)] = true;
    used.types[typeTag(p)] = true;
    used.vendors[slug(p.v)] = true;
  }

  function buildStack(result, facets) {
    var slots = selectSlots(result, facets);
    var handles = [];
    slots.forEach(function (s) { s.cols.forEach(function (c) { if (handles.indexOf(c) === -1) handles.push(c); }); });
    return Promise.all(handles.map(fetchFeed)).then(function (feeds) {
      var byHandle = {};
      handles.forEach(function (hnd, i) { byHandle[hnd] = feeds[i] || []; });
      var used = { ids: {}, families: {}, types: {}, vendors: {} };
      var filled = [];
      slots.forEach(function (slot) {
        var pool = [];
        var seen = {};
        slot.cols.forEach(function (c) {
          (byHandle[c] || []).forEach(function (p, idx) {
            if (seen[p.id]) return;
            seen[p.id] = true;
            p._idx = idx;
            pool.push(p);
          });
        });
        var ranked = rankSlot(slot, pool, facets, used);
        if (!ranked) return;
        markUsed(used, ranked.pick);
        filled.push({ slot: slot, pick: ranked.pick, alts: ranked.alts, flavours: ranked.flavours, pool: pool, removed: false });
      });
      return filled;
    });
  }

  /* --------------------------------------------------------------- the quiz */

  function initQuiz(root) {
    if (!root || root.dataset.gsInit) return;
    root.dataset.gsInit = '1';

    var cfgEl = qs(root, '[data-gs-config]');
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    var S = cfg.settings || {};
    var L = S.labels || {};
    var results = (cfg.results || []).filter(function (r) { return r.key; });
    if (!results.length) return;
    var resultByKey = {};
    results.forEach(function (r) { resultByKey[r.key] = r; });
    var defaultResult = results.filter(function (r) { return r.default; })[0] || results[0];
    var track = makeTracker(!!S.analytics);
    var money = function (cents) { return formatMoney(cents, S.moneyFormat); };

    var welcome = qs(root, '[data-gs-welcome]');
    var startScreen = qs(root, '[data-gs-start]');
    var form = qs(root, '[data-gs-form]');
    var stepEls = qsa(root, '[data-gs-step]');
    var progress = qs(root, '[data-gs-progress]');
    var stepCount = qs(root, '[data-gs-step-count]');
    var backBtn = qs(root, '[data-gs-back]');
    var nextBtn = qs(root, '[data-gs-next]');
    var resultsEl = qs(root, '[data-gs-results]');
    var resultsHeading = qs(root, '[data-gs-results-heading]');
    var resultsDesc = qs(root, '[data-gs-results-desc]');
    var summaryWrap = qs(root, '[data-gs-summary]');
    var summaryChips = qs(root, '[data-gs-summary-chips]');
    var stackList = qs(root, '[data-gs-stack]');
    var emptyEl = qs(root, '[data-gs-empty]');
    var routineEl = qs(root, '[data-gs-routine]');
    var timelineEl = qs(root, '[data-gs-timeline]');
    var moreWrap = qs(root, '[data-gs-more]');
    var moreGrid = qs(root, '[data-gs-more-grid]');
    var subtotalEl = qs(root, '[data-gs-subtotal]');
    var countLabelEl = qs(root, '[data-gs-count-label]');
    var savingRow = qs(root, '[data-gs-saving-row]');
    var savingLabelEl = qs(root, '[data-gs-saving-label]');
    var savingEl = qs(root, '[data-gs-saving]');
    var totalEl = qs(root, '[data-gs-total]');
    var deliveryEl = qs(root, '[data-gs-delivery]');
    var deliveryFill = qs(root, '[data-gs-delivery-fill]');
    var deliveryText = qs(root, '[data-gs-delivery-text]');
    var bundleBtn = qs(root, '[data-gs-bundle]');
    var bundleShop = qs(root, '[data-gs-bundle-shop]');
    var bundleNote = qs(root, '[data-gs-bundle-note]');
    var mbar = qs(root, '[data-gs-mbar]');
    var mbarCount = qs(root, '[data-gs-mbar-count]');
    var mbarTotal = qs(root, '[data-gs-mbar-total]');
    var mbarAdd = qs(root, '[data-gs-mbar-add]');
    var sheet = qs(root, '[data-gs-sheet]');
    var sheetPanel = qs(root, '[data-gs-sheet-panel]');
    var sheetTitle = qs(root, '[data-gs-sheet-title]');
    var sheetSub = qs(root, '[data-gs-sheet-sub]');
    var sheetList = qs(root, '[data-gs-sheet-list]');
    var beginBtn = qs(root, '[data-gs-begin]');
    var shareBtn = qs(root, '[data-gs-share]');
    var restartBtns = qsa(root, '[data-gs-restart]');
    var viewSavedBtn = qs(root, '[data-gs-view-saved]');
    var dismissSavedBtn = qs(root, '[data-gs-dismiss-saved]');
    var emailWrap = qs(root, '[data-gs-email]');
    var emailForm = emailWrap ? qs(emailWrap, 'form') : null;
    var emailTags = qs(root, '[data-gs-email-tags]');
    var emailNote = qs(root, '[data-gs-email-note]');
    var emailReturn = qs(root, '[data-gs-email-return]');
    var emailSuccess = qs(root, '[data-gs-email-success]');

    if (!form || !stepEls.length || !stackList) return;

    var autoAdvance = S.autoAdvance !== false;

    var steps = stepEls.map(function (el, i) {
      var opts = qsa(el, '[data-gs-option]').map(function (o) {
        return {
          el: o,
          i: parseInt(o.dataset.i, 10),
          outcomes: parseOutcomes(o.dataset.o),
          facets: parseFacets(o.dataset.f),
          chip: (o.dataset.c || '').trim(),
          label: (qs(o, '.gsq__option-label') || o).textContent.trim(),
          none: o.hasAttribute('data-none')
        };
      });
      return {
        i: i,
        el: el,
        key: (el.dataset.key || String(i)).trim(),
        multi: el.hasAttribute('data-multi'),
        show: parseFacets((el.dataset.show || '').replace(/,/g, ';')),
        opts: opts
      };
    });
    var stepByKey = {};
    steps.forEach(function (s) { stepByKey[s.key] = s; });

    /* ------------------------------------------------------------- state */
    var answers = {};          // step index -> [option i, ...]
    var pos = 0;
    var editing = false;       // single-step edit from results
    var currentKey = null;     // outcome key being shown
    var overrideKey = null;    // user picked an alternative stack
    var ranked = [];
    var stack = [];            // filled slots
    var buildToken = 0;
    var advanceTimer = null;
    var emailPosted = false;

    function optionsOf(step) { return (answers[step.i] || []).map(function (i) { return step.opts.filter(function (o) { return o.i === i; })[0]; }).filter(Boolean); }

    function facetsUpTo(limitIndex) {
      var f = {};
      steps.forEach(function (step) {
        if (limitIndex !== undefined && step.i >= limitIndex) return;
        if (!isVisible(step, f)) return;
        optionsOf(step).forEach(function (o) {
          o.facets.forEach(function (fc) {
            if (fc.k === 'none') return;
            f[fc.k] = f[fc.k] || [];
            if (f[fc.k].indexOf(fc.v) === -1) f[fc.k].push(fc.v);
          });
        });
      });
      return f;
    }

    function isVisible(step, facets) {
      if (!step.show.length) return true;
      for (var i = 0; i < step.show.length; i++) {
        if (facetHas(facets, step.show[i].k, step.show[i].v)) return true;
      }
      return false;
    }

    function activeSteps() {
      var f = {};
      var list = [];
      steps.forEach(function (step) {
        if (!isVisible(step, f)) return;
        list.push(step);
        optionsOf(step).forEach(function (o) {
          o.facets.forEach(function (fc) {
            if (fc.k === 'none') return;
            f[fc.k] = f[fc.k] || [];
            if (f[fc.k].indexOf(fc.v) === -1) f[fc.k].push(fc.v);
          });
        });
      });
      return list;
    }

    function currentFacets() { return facetsUpTo(); }

    function computeRanked() {
      var scores = {};
      var order = [];
      var active = activeSteps();
      active.forEach(function (step) {
        optionsOf(step).forEach(function (o) {
          o.outcomes.forEach(function (oc) {
            if (!resultByKey[oc.k]) return;
            if (!(oc.k in scores)) { scores[oc.k] = 0; order.push(oc.k); }
            scores[oc.k] += oc.w;
          });
        });
      });
      // Tie-break: outcomes named by the first (goal) question win, then config order.
      var firstOutcomes = {};
      var s0 = active[0];
      if (s0) optionsOf(s0).forEach(function (o) { o.outcomes.forEach(function (oc) { firstOutcomes[oc.k] = true; }); });
      return order.slice().sort(function (a, b) {
        var d = scores[b] - scores[a];
        if (d) return d;
        var fa = firstOutcomes[a] ? 1 : 0, fb = firstOutcomes[b] ? 1 : 0;
        if (fa !== fb) return fb - fa;
        return order.indexOf(a) - order.indexOf(b);
      });
    }

    /* ----------------------------------------------------- persistence */
    function encodeAnswers() {
      return steps.filter(function (s) { return answers[s.i] && answers[s.i].length; })
        .map(function (s) { return s.key + '.' + answers[s.i].join('.'); }).join('_');
    }

    function decodeAnswers(code) {
      var out = {};
      String(code || '').split('_').forEach(function (part) {
        var bits = part.split('.');
        var step = stepByKey[bits[0]];
        if (!step) return;
        var idx = bits.slice(1).map(function (n) { return parseInt(n, 10); }).filter(function (n) {
          return step.opts.some(function (o) { return o.i === n; });
        });
        if (idx.length) out[step.i] = step.multi ? idx : [idx[0]];
      });
      return out;
    }

    function shareUrl(extra) {
      var url = location.origin + location.pathname;
      var code = encodeAnswers();
      var params = [];
      if (code) params.push('a=' + encodeURIComponent(code));
      if (overrideKey) params.push('s=' + encodeURIComponent(overrideKey));
      if (extra) params.push(extra);
      return params.length ? url + '?' + params.join('&') : url;
    }

    function save() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ a: encodeAnswers(), s: overrideKey, t: Date.now() }));
      } catch (e) { /* private mode */ }
    }

    function loadSaved() {
      try {
        var raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        var data = JSON.parse(raw);
        if (!data || !data.a) return null;
        return data;
      } catch (e) { return null; }
    }

    /* ------------------------------------------------------- questions */
    function syncOptionStates(step) {
      var chosen = answers[step.i] || [];
      step.opts.forEach(function (o) {
        var on = chosen.indexOf(o.i) !== -1;
        o.el.setAttribute('aria-pressed', on ? 'true' : 'false');
        o.el.classList.toggle('is-selected', on);
      });
    }

    function renderProgress(list) {
      var fill = progress && qs(progress, '[data-gs-progress-fill]');
      if (!fill || !list.length) return;
      var pct = Math.round(((pos + 1) / list.length) * 100);
      fill.style.width = pct + '%';
      progress.setAttribute('aria-valuenow', String(pct));
    }

    function setFlow(on) {
      document.body.classList.toggle('gsq-flow', !!on);
    }

    function updateNext(list) {
      var step = list[pos];
      var answered = step && answers[step.i] && answers[step.i].length;
      nextBtn.disabled = !answered;
      var last = pos === list.length - 1;
      nextBtn.textContent = (last || editing) ? nextBtn.dataset.finishLabel : nextBtn.dataset.nextLabel;
    }

    function showStep(newPos) {
      var list = activeSteps();
      pos = Math.max(0, Math.min(newPos, list.length - 1));
      steps.forEach(function (s) { s.el.hidden = true; });
      var step = list[pos];
      if (!step) return;
      step.el.hidden = false;
      syncOptionStates(step);
      form.classList.toggle('is-multi', !!step.multi);
      renderProgress(list);
      if (stepCount) {
        stepCount.textContent = (pos + 1) + ' / ' + list.length;
        stepCount.setAttribute('aria-label', (S.questionWord || 'Question') + ' ' + (pos + 1) + ' ' + (S.ofWord || 'of') + ' ' + list.length);
      }
      backBtn.disabled = pos === 0 && !editing;
      updateNext(list);
      var legend = qs(step.el, '.gsq__question');
      if (legend) { legend.setAttribute('tabindex', '-1'); legend.focus({ preventScroll: true }); }
    }

    function selectOption(step, opt) {
      if (step.multi) {
        var cur = answers[step.i] ? answers[step.i].slice() : [];
        if (opt.none) {
          cur = cur.indexOf(opt.i) !== -1 ? [] : [opt.i];
        } else {
          cur = cur.filter(function (i) { var o = step.opts.filter(function (x) { return x.i === i; })[0]; return o && !o.none; });
          var at = cur.indexOf(opt.i);
          if (at === -1) cur.push(opt.i); else cur.splice(at, 1);
        }
        answers[step.i] = cur;
      } else {
        answers[step.i] = [opt.i];
      }
      syncOptionStates(step);
      track('answer', { step: step.key, option: opt.i, label: opt.label });
      var list = activeSteps();
      updateNext(list);
      if (!step.multi) {
        if (editing) {
          if (autoAdvance) {
            if (advanceTimer) clearTimeout(advanceTimer);
            advanceTimer = setTimeout(finish, 220);
          }
          return;
        }
        if (autoAdvance) {
          if (advanceTimer) clearTimeout(advanceTimer);
          advanceTimer = setTimeout(function () {
            var current = activeSteps();
            if (pos < current.length - 1) showStep(pos + 1);
            else finish();
          }, 260);
        }
      }
    }

    steps.forEach(function (step) {
      step.opts.forEach(function (opt, idx) {
        opt.el.addEventListener('click', function () { selectOption(step, opt); });
        opt.el.addEventListener('keydown', function (e) {
          var delta = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : (e.key === 'ArrowLeft' || e.key === 'ArrowUp') ? -1 : 0;
          if (!delta) return;
          e.preventDefault();
          var next = step.opts[(idx + delta + step.opts.length) % step.opts.length];
          if (next) next.el.focus();
        });
      });
    });

    function getStickyHeaderOffset() {
      if (!document.querySelector('store-header[data-is-sticky="true"]')) return 0;
      var section = root.closest('.shopify-section');
      var styles = section ? getComputedStyle(section) : getComputedStyle(document.documentElement);
      return parseInt(styles.getPropertyValue('--header-height').replace('px', ''), 10) || 0;
    }

    function scrollToPageTop() {
      requestAnimationFrame(function () {
        var top = Math.max(0, root.getBoundingClientRect().top + window.scrollY - getStickyHeaderOffset());
        window.scrollTo({ top: top, behavior: 'auto' });
      });
    }

    function showForm() {
      root.classList.add('is-active');
      setFlow(true);
      if (welcome) welcome.hidden = true;
      if (startScreen) startScreen.hidden = true;
      resultsEl.hidden = true;
      if (mbar) mbar.hidden = true;
      form.hidden = false;
    }

    function startQuiz() {
      editing = false;
      showForm();
      showStep(0);
      scrollToPageTop();
      track('start', {});
    }

    if (beginBtn) beginBtn.addEventListener('click', startQuiz);

    nextBtn.addEventListener('click', function () {
      var list = activeSteps();
      var step = list[pos];
      if (!step || !answers[step.i] || !answers[step.i].length) return;
      if (editing || pos === list.length - 1) finish();
      else showStep(pos + 1);
    });

    backBtn.addEventListener('click', function () {
      if (advanceTimer) clearTimeout(advanceTimer);
      if (editing) { applyAnswers(); return; }
      if (pos > 0) showStep(pos - 1);
    });

    function editStep(step) {
      editing = true;
      showForm();
      var list = activeSteps();
      var at = list.indexOf(step);
      showStep(at === -1 ? 0 : at);
      scrollToPageTop();
      track('edit', { step: step.key });
    }

    // Recompute the winning outcome from current answers and render.
    function applyAnswers() {
      if (advanceTimer) clearTimeout(advanceTimer);
      editing = false;
      overrideKey = null;
      ranked = computeRanked();
      save();
      if (history.replaceState) history.replaceState(null, '', shareUrl());
      renderResults();
    }

    function finish() {
      track('complete', { outcome: computeRanked()[0] || defaultResult.key, answers: encodeAnswers() });
      applyAnswers();
    }

    /* --------------------------------------------------------- results */
    function activePicks() { return stack.filter(function (row) { return !row.removed; }); }

    function totals() {
      var picks = activePicks();
      var subtotal = 0, saving = 0;
      picks.forEach(function (row) { subtotal += row.pick.p; });
      if (S.percent > 0 && picks.length >= (S.minItems || 2)) {
        picks.forEach(function (row) { saving += Math.floor(row.pick.p * S.percent / 100); });
      }
      return { count: picks.length, subtotal: subtotal, saving: saving, total: subtotal - saving };
    }

    function renderTotals() {
      var t = totals();
      var countText = t.count === 1 ? (L.item || '1 product') : fill(L.items || '[count] products', { count: t.count });
      if (countLabelEl) countLabelEl.textContent = countText;
      if (subtotalEl) subtotalEl.textContent = money(t.subtotal);
      if (savingRow) {
        savingRow.hidden = !(t.saving > 0);
        if (savingLabelEl) savingLabelEl.textContent = fill(L.saving || 'Stack saving ([percent]%)', { percent: S.percent });
        if (savingEl) savingEl.textContent = '−' + money(t.saving);
      }
      if (totalEl) totalEl.textContent = money(t.total);
      if (deliveryEl) {
        var threshold = Number(S.threshold) || 0;
        if (threshold > 0 && t.count > 0) {
          deliveryEl.hidden = false;
          var ratio = Math.min(1, t.total / threshold);
          if (deliveryFill) deliveryFill.style.width = Math.round(ratio * 100) + '%';
          deliveryEl.classList.toggle('is-unlocked', t.total >= threshold);
          if (deliveryText) {
            deliveryText.textContent = t.total >= threshold
              ? (L.deliveryUnlocked || 'Free UK delivery unlocked')
              : fill(L.deliveryMore || 'Add [amount] more for free UK delivery', { amount: money(threshold - t.total) });
          }
        } else {
          deliveryEl.hidden = true;
        }
      }
      if (bundleBtn) bundleBtn.disabled = t.count === 0;
      if (mbar) {
        if (mbarCount) mbarCount.textContent = countText;
        if (mbarTotal) mbarTotal.textContent = money(t.total);
        if (mbarAdd) mbarAdd.disabled = t.count === 0;
      }
      if (emptyEl) emptyEl.hidden = stack.length > 0 || stackList.getAttribute('aria-busy') === 'true';
      updateEmailFields();
    }

    function priceEl(p) {
      var wrap = h('span', { class: 'gsq__item-price' });
      if (p.cp && p.cp > p.p) {
        wrap.appendChild(h('span', { class: 'gsq__item-price-sale', text: money(p.p) }));
        wrap.appendChild(h('s', { class: 'gsq__item-price-was', text: money(p.cp) }));
      } else {
        wrap.appendChild(h('span', { text: money(p.p) }));
      }
      return wrap;
    }

    function formatBadge(p) {
      var f = detectFormat(p);
      var names = { powder: 'Powder', caps: 'Capsules', snack: 'Snack', drink: 'Drink', other: '' };
      return names[f] || '';
    }

    function renderItem(row, index) {
      var p = row.pick;
      var li = h('li', { class: 'gsq__item' + (index === 0 ? ' is-core' : '') + (row.removed ? ' is-removed' : ''), 'data-gs-item': '', 'data-product-handle': p.h });

      var media = h('a', { class: 'gsq__item-media', href: p.u, tabindex: '-1', 'aria-hidden': 'true' },
        p.img ? h('img', { class: 'gsq__item-img', src: p.img, alt: '', width: '76', height: '76', loading: 'lazy' }) : null);

      var body = h('div', { class: 'gsq__item-body' });
      body.appendChild(h('span', { class: 'gsq__item-role', text: row.slot.label }));
      body.appendChild(h('a', { class: 'gsq__item-title', href: p.u, text: p.t }));
      var why = row.slot.why || p.b || p.s;
      if (why) body.appendChild(h('p', { class: 'gsq__item-why', text: why }));

      var meta = h('div', { class: 'gsq__item-meta' });
      meta.appendChild(priceEl(p));
      var badge = formatBadge(p);
      if (badge) meta.appendChild(h('span', { class: 'gsq__item-badge', text: badge }));
      if (p.r >= 4 && p.rc > 0) meta.appendChild(h('span', { class: 'gsq__item-badge gsq__item-badge--rating', text: Number(p.r).toFixed(1) + '★' }));
      body.appendChild(meta);

      if (row.flavours && row.flavours.length > 1) {
        var selId = 'gsq-flav-' + index + '-' + p.id;
        var select = h('select', { class: 'gsq__flavour-select', id: selId, 'aria-label': L.flavour || 'Flavour' });
        row.flavours.forEach(function (fp) {
          var label = flavourOf(fp) || fp.t;
          var o = h('option', { value: String(fp.id), text: label });
          if (fp.id === p.id) o.selected = true;
          select.appendChild(o);
        });
        select.addEventListener('change', function () {
          var next = row.flavours.filter(function (fp) { return String(fp.id) === select.value; })[0];
          if (!next) return;
          row.pick = next;
          track('flavour', { slot: row.slot.key, handle: next.h });
          renderStack();
        });
        var flavWrap = h('label', { class: 'gsq__flavour', for: selId }, [h('span', { class: 'gsq__flavour-label', text: L.flavour || 'Flavour' }), select]);
        body.appendChild(flavWrap);
      }

      var actions = h('div', { class: 'gsq__item-actions' });
      if (!row.removed) {
        var addBtn = h('button', { type: 'button', class: 'btn btn--secondary gsq__item-add', text: L.add || 'Add' });
        addBtn.addEventListener('click', function () {
          if (addBtn.classList.contains('is-loading')) return;
          addBtn.classList.add('is-loading');
          addToCart([{ id: row.pick.vid, quantity: 1 }]).then(function () {
            addBtn.classList.remove('is-loading');
            addBtn.classList.add('is-added');
            addBtn.textContent = L.added || 'Added';
            track('add_item', { outcome: currentKey, slot: row.slot.key, handle: row.pick.h, value: row.pick.p / 100 });
            setTimeout(function () { addBtn.classList.remove('is-added'); addBtn.textContent = L.add || 'Add'; }, 2200);
          }).catch(function () { addBtn.classList.remove('is-loading'); });
        });
        actions.appendChild(addBtn);
        if (row.alts && row.alts.length) {
          var swapBtn = h('button', { type: 'button', class: 'gsq__item-link', text: L.swap || 'Swap' });
          swapBtn.addEventListener('click', function () { openSheet(row, swapBtn); });
          actions.appendChild(swapBtn);
        }
        var removeBtn = h('button', { type: 'button', class: 'gsq__item-link gsq__item-link--muted', text: L.remove || 'Remove' });
        removeBtn.addEventListener('click', function () {
          row.removed = true;
          track('remove', { outcome: currentKey, slot: row.slot.key, handle: row.pick.h });
          renderStack();
        });
        actions.appendChild(removeBtn);
      } else {
        actions.appendChild(h('span', { class: 'gsq__item-removed', text: L.removed || 'Removed from your stack' }));
        var restoreBtn = h('button', { type: 'button', class: 'btn btn--secondary gsq__item-add', text: L.restore || 'Add back' });
        restoreBtn.addEventListener('click', function () { row.removed = false; renderStack(); });
        actions.appendChild(restoreBtn);
      }

      li.appendChild(media);
      li.appendChild(body);
      li.appendChild(actions);
      return li;
    }

    function renderStack() {
      stackList.innerHTML = '';
      stackList.setAttribute('aria-busy', 'false');
      stack.forEach(function (row, i) { stackList.appendChild(renderItem(row, i)); });
      renderTimeline();
      renderTotals();
    }

    function renderSkeleton(n) {
      stackList.innerHTML = '';
      stackList.setAttribute('aria-busy', 'true');
      for (var i = 0; i < n; i++) {
        stackList.appendChild(h('li', { class: 'gsq__item gsq__item--skeleton', 'aria-hidden': 'true' }, [
          h('span', { class: 'gsq__sk gsq__sk--media' }),
          h('span', { class: 'gsq__sk-body' }, [h('span', { class: 'gsq__sk gsq__sk--role' }), h('span', { class: 'gsq__sk gsq__sk--title' }), h('span', { class: 'gsq__sk gsq__sk--why' })]),
          h('span', { class: 'gsq__sk gsq__sk--btn' })
        ]));
      }
      stackList.appendChild(h('li', { class: 'gsq__loading', role: 'status', text: L.loading || 'Building your stack…' }));
      if (emptyEl) emptyEl.hidden = true;
    }

    function renderTimeline() {
      if (!routineEl || !timelineEl) return;
      var picks = activePicks();
      timelineEl.innerHTML = '';
      if (!picks.length) { routineEl.hidden = true; return; }
      var groups = {};
      picks.forEach(function (row) {
        var when = TIMING_ORDER.indexOf(row.slot.when) !== -1 ? row.slot.when : 'anytime';
        groups[when] = groups[when] || [];
        groups[when].push(row);
      });
      TIMING_ORDER.forEach(function (when) {
        if (!groups[when]) return;
        var li = h('li', { class: 'gsq__timeline-item gsq__timeline-item--' + when });
        li.appendChild(h('span', { class: 'gsq__timeline-when', text: (L.timing && L.timing[when]) || when }));
        var ul = h('ul', { class: 'gsq__timeline-list' });
        groups[when].forEach(function (row) {
          ul.appendChild(h('li', {}, [h('strong', { text: row.slot.label }), document.createTextNode(' — ' + row.pick.t)]));
        });
        li.appendChild(ul);
        timelineEl.appendChild(li);
      });
      routineEl.hidden = false;
    }

    function renderSummary() {
      if (!summaryWrap || !summaryChips) return;
      summaryChips.innerHTML = '';
      var any = false;
      activeSteps().forEach(function (step) {
        var opts = optionsOf(step);
        if (!opts.length) return;
        var labels = opts.map(function (o) { return o.chip; }).filter(Boolean);
        if (!labels.length) return;
        any = true;
        var chip = h('button', { type: 'button', class: 'gsq__chip', 'aria-label': labels.join(', ') + ' — ' + (L.change || 'Change') }, [
          h('span', { text: labels.join(', ') }),
          h('span', { class: 'gsq__chip-change', text: L.change || 'Change' })
        ]);
        chip.addEventListener('click', function () { editStep(step); });
        summaryChips.appendChild(chip);
      });
      summaryWrap.hidden = !any;
    }

    function renderAlternatives() {
      if (!moreWrap || !moreGrid) return;
      moreGrid.innerHTML = '';
      var shown = 0;
      var list = ranked.length ? ranked : results.map(function (r) { return r.key; });
      list.forEach(function (key) {
        if (key === currentKey || shown >= 2 || !resultByKey[key]) return;
        var r = resultByKey[key];
        var card = h('div', { class: 'gsq__more-card' }, [
          h('span', { class: 'gsq__more-card-title', text: r.title }),
          r.card ? h('span', { class: 'gsq__more-card-text', text: r.card }) : null
        ]);
        var btn = h('button', { type: 'button', class: 'gsq__more-card-cta', text: L.alternativesCta || 'Show this stack' });
        btn.addEventListener('click', function () {
          overrideKey = key;
          save();
          history.replaceState && history.replaceState(null, '', shareUrl());
          track('switch', { from: currentKey, to: key });
          renderResults();
        });
        card.appendChild(btn);
        moreGrid.appendChild(card);
        shown++;
      });
      moreWrap.hidden = shown === 0;
    }

    function updateEmailFields() {
      if (!emailForm) return;
      var code = encodeAnswers();
      var result = resultByKey[currentKey] || defaultResult;
      var handles = activePicks().map(function (row) { return row.pick.h; });
      if (emailTags) emailTags.value = 'prospect, newsletter, quiz, quiz:' + (currentKey || '') + (code ? ', quiz-a:' + code : '');
      if (emailNote) emailNote.value = 'Find Your Stack — ' + result.title + ' — ' + shareUrl() + (handles.length ? ' — ' + handles.join(', ') : '');
      if (emailReturn) emailReturn.value = shareUrl('saved=1');
    }

    function renderResults() {
      var key = overrideKey || ranked[0];
      var result = resultByKey[key] || defaultResult;
      currentKey = result.key;

      root.classList.add('is-active');
      setFlow(false);
      if (welcome) welcome.hidden = true;
      if (startScreen) startScreen.hidden = true;
      form.hidden = true;
      resultsEl.hidden = false;
      if (mbar) mbar.hidden = false;

      if (resultsHeading) resultsHeading.textContent = result.title || '';
      if (resultsDesc) resultsDesc.innerHTML = result.desc || '';
      if (bundleShop) {
        bundleShop.href = result.url || bundleShop.href;
        if (result.cta) bundleShop.textContent = result.cta;
      }

      renderSummary();
      renderAlternatives();
      var facets = currentFacets();
      var expected = selectSlots(result, facets).length || 3;
      renderSkeleton(expected);
      if (routineEl) routineEl.hidden = true;
      stack = [];
      renderTotals();

      var token = ++buildToken;
      buildStack(result, facets).then(function (filled) {
        if (token !== buildToken) return;
        stack = filled;
        renderStack();
        track('stack', { outcome: currentKey, items: filled.map(function (r) { return r.pick.h; }), value: totals().total / 100 });
      }).catch(function () {
        if (token !== buildToken) return;
        stack = [];
        renderStack();
      });

      if (resultsHeading) resultsHeading.focus({ preventScroll: true });
      scrollToPageTop();
    }

    /* ----------------------------------------------------------- sheet */
    var sheetReturnFocus = null;
    function closeSheet() {
      if (!sheet) return;
      sheet.hidden = true;
      document.documentElement.classList.remove('gsq-sheet-open');
      if (sheetReturnFocus && sheetReturnFocus.focus) sheetReturnFocus.focus();
      sheetReturnFocus = null;
    }

    function openSheet(row, returnFocus) {
      if (!sheet || !sheetList) return;
      sheetReturnFocus = returnFocus || null;
      if (sheetTitle) sheetTitle.textContent = (L.swap || 'Swap') + ' · ' + row.slot.label;
      if (sheetSub) sheetSub.textContent = L.sheetSub || '';
      sheetList.innerHTML = '';
      var options = [row.pick].concat(row.alts);
      options.forEach(function (p, i) {
        var isCurrent = i === 0;
        var li = h('li', { class: 'gsq__sheet-item' + (isCurrent ? ' is-current' : '') });
        li.appendChild(h('span', { class: 'gsq__sheet-media' }, p.img ? h('img', { src: p.img, alt: '', width: '56', height: '56', loading: 'lazy' }) : null));
        var body = h('span', { class: 'gsq__sheet-body' }, [
          h('span', { class: 'gsq__sheet-title-text', text: p.t }),
          h('span', { class: 'gsq__sheet-meta' }, [
            priceEl(p),
            formatBadge(p) ? h('span', { class: 'gsq__item-badge', text: formatBadge(p) }) : null,
            p.r >= 4 && p.rc > 0 ? h('span', { class: 'gsq__item-badge gsq__item-badge--rating', text: Number(p.r).toFixed(1) + '★' }) : null,
            isVeganish(p) ? h('span', { class: 'gsq__item-badge', text: 'Vegan' }) : null,
            isStimFree(p) && row.slot.stim ? h('span', { class: 'gsq__item-badge', text: 'Stim-free' }) : null
          ]),
          p.b || p.s ? h('span', { class: 'gsq__sheet-why', text: p.b || p.s }) : null
        ]);
        li.appendChild(body);
        if (isCurrent) {
          li.appendChild(h('span', { class: 'gsq__sheet-current', text: L.current || 'Current pick' }));
        } else {
          var choose = h('button', { type: 'button', class: 'btn btn--primary gsq__sheet-choose', text: L.choose || 'Choose' });
          choose.addEventListener('click', function () {
            var prev = row.pick;
            row.pick = p;
            row.alts = [prev].concat(row.alts.filter(function (a) { return a.id !== p.id; })).slice(0, 4);
            row.flavours = row.pool.filter(function (x) { return familyKey(x) === familyKey(p) && x.vid; })
              .sort(function (a, b) { return (flavourOf(a) || '').localeCompare(flavourOf(b) || ''); });
            track('swap', { outcome: currentKey, slot: row.slot.key, from: prev.h, to: p.h });
            closeSheet();
            renderStack();
          });
          li.appendChild(choose);
        }
        sheetList.appendChild(li);
      });
      sheet.hidden = false;
      document.documentElement.classList.add('gsq-sheet-open');
      if (sheetPanel) sheetPanel.focus({ preventScroll: true });
    }

    if (sheet) {
      qsa(sheet, '[data-gs-sheet-close]').forEach(function (el) { el.addEventListener('click', closeSheet); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !sheet.hidden) closeSheet(); });
    }

    /* ------------------------------------------------------ bundle add */
    function addStack(btn) {
      var picks = activePicks();
      if (!picks.length || btn.classList.contains('is-loading')) return;
      var items = picks.map(function (row) { return { id: row.pick.vid, quantity: 1 }; });
      var original = btn.dataset.addLabel || btn.textContent;
      btn.classList.add('is-loading');
      btn.textContent = btn.dataset.addingLabel || 'Adding…';
      var t = totals();
      addToCart(items).then(function () {
        btn.classList.remove('is-loading');
        btn.textContent = btn.dataset.addedLabel || 'Added to cart';
        track('add_stack', { outcome: currentKey, items: picks.map(function (r) { return r.pick.h; }), count: picks.length, value: t.total / 100 });
        fetchCart().then(function (cart) {
          if (cart && bundleNote && S.percent > 0) {
            var applied = Number(cart.total_discount) > 0;
            bundleNote.textContent = applied
              ? fill(L.saving || 'Stack saving ([percent]%)', { percent: S.percent }) + ' · ' + money(cart.total_discount)
              : bundleNote.textContent;
          }
        });
        setTimeout(function () { btn.textContent = original; }, 2600);
      }).catch(function () {
        btn.classList.remove('is-loading');
        btn.textContent = original;
      });
    }
    if (bundleBtn) bundleBtn.addEventListener('click', function () { addStack(bundleBtn); });
    if (mbarAdd) mbarAdd.addEventListener('click', function () { addStack(bundleBtn || mbarAdd); if (bundleBtn) { mbarAdd.textContent = bundleBtn.textContent; setTimeout(function () { mbarAdd.textContent = bundleBtn.dataset.addLabel || mbarAdd.textContent; }, 2600); } });

    /* ----------------------------------------------------------- share */
    function fallbackCopy(text, done) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) { /* no-op */ }
      document.body.removeChild(ta);
      done();
    }

    if (shareBtn) {
      shareBtn.addEventListener('click', function () {
        var url = shareUrl();
        function done() {
          shareBtn.classList.add('is-copied');
          shareBtn.textContent = shareBtn.dataset.copiedLabel;
          track('share', { outcome: currentKey });
          setTimeout(function () { shareBtn.classList.remove('is-copied'); shareBtn.textContent = shareBtn.dataset.shareLabel; }, 2500);
        }
        if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) {
          navigator.share({ title: document.title, url: url }).then(done).catch(function () {
            if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(done, function () { fallbackCopy(url, done); });
            else fallbackCopy(url, done);
          });
          return;
        }
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(done, function () { fallbackCopy(url, done); });
        else fallbackCopy(url, done);
      });
    }

    restartBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        answers = {};
        overrideKey = null;
        ranked = [];
        stack = [];
        steps.forEach(syncOptionStates);
        history.replaceState && history.replaceState(null, '', location.pathname);
        track('restart', {});
        startQuiz();
      });
    });

    /* ----------------------------------------------------------- email */
    if (emailForm) {
      emailForm.addEventListener('submit', function () {
        updateEmailFields();
        save();
        track('email_submit', { outcome: currentKey });
      });
    }

    function showEmailSuccess() {
      if (!emailWrap) return;
      if (emailForm) emailForm.hidden = true;
      if (emailSuccess) emailSuccess.hidden = false;
      emailPosted = true;
      track('email', { outcome: currentKey });
    }

    /* ---------------------------------------------------------- welcome */
    if (viewSavedBtn) {
      viewSavedBtn.addEventListener('click', function () {
        var saved = loadSaved();
        if (!saved) { startQuiz(); return; }
        answers = decodeAnswers(saved.a);
        overrideKey = saved.s || null;
        ranked = computeRanked();
        history.replaceState && history.replaceState(null, '', shareUrl());
        track('resume', {});
        renderResults();
      });
    }
    if (dismissSavedBtn) dismissSavedBtn.addEventListener('click', startQuiz);

    /* ------------------------------------------------------------- boot */
    var params = new URLSearchParams(location.search);
    var code = params.get('a');
    var legacy = params.get('r');
    var switched = params.get('s');
    if (code) {
      answers = decodeAnswers(code);
      if (Object.keys(answers).length) {
        overrideKey = switched && resultByKey[switched] ? switched : null;
        ranked = computeRanked();
        save();
        renderResults();
        if (params.get('saved') === '1' || params.get('customer_posted') === 'true') showEmailSuccess();
        return;
      }
    }
    if (legacy) {
      var legacyKey = legacy.split('.')[0];
      if (resultByKey[legacyKey]) {
        overrideKey = legacyKey;
        ranked = [legacyKey];
        renderResults();
        return;
      }
    }
    if (params.get('customer_posted') === 'true') {
      var savedAfterPost = loadSaved();
      if (savedAfterPost) {
        answers = decodeAnswers(savedAfterPost.a);
        overrideKey = savedAfterPost.s || null;
        ranked = computeRanked();
        if (history.replaceState) history.replaceState(null, '', shareUrl());
        renderResults();
        showEmailSuccess();
        return;
      }
    }
    if (loadSaved() && welcome) welcome.hidden = false;
  }

  function init() {
    qsa(document, '[data-gs-quiz]').forEach(initQuiz);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', function (evt) {
    var el = evt.target.querySelector ? evt.target.querySelector('[data-gs-quiz]') : null;
    if (el) initQuiz(el);
  });
})();
